import { FormDataAPIType } from 'components/common/form';
import React, { useContext } from 'react';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import BlockEditPanelControls from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelControls';
import useBundleTranslation from 'i18n';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { prepareFormElementProps } from 'components/common/form/formTools';
import { Box, FormLabel, Stack } from '@mui/material';
import BlockEditPanelHeader from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelHeader';
import EditPanelCheckBox from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelCheckBox';
import { EditPanelContext } from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanel';
import { type ComponentSettingsKpiChartMeasureField } from '@report-content';

export default function MeasureFieldForm({
    field,
    onApply,
    onCancel,
    title,
}: {
    field: ComponentSettingsKpiChartMeasureField;
    onApply: (field: ComponentSettingsKpiChartMeasureField) => void;
    onCancel: () => void;
    title: string;
}) {
    const editPanelContext = useContext(EditPanelContext);
    const numericMasks = (editPanelContext?.displayMasks ?? []).filter((d) => d.mask_type == 'numeric');
    const elementProps: FormDataAPIType = useCustomSimplifiedForm({
        custom_label: field.custom_label ?? '',
        show_data_labels: field.show_data_labels ?? 'N',
        display_mask_id: numericMasks.some((d) => d.display_mask_id == field.display_mask_id)
            ? field.display_mask_id
            : '0',
    });
    const { t } = useBundleTranslation(['components/report-content']);

    return (
        <Stack height={1}>
            <Box flexShrink={0}>
                <BlockEditPanelHeader title={title} onCancel={() => onCancel()} item={field.column_name} />
            </Box>
            <Stack sx={{ overflow: 'auto', flexGrow: 1, p: 3 }} spacing={2}>
                <Box>
                    <ReactHookFormController
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                component: 'FormText',
                                label: t('kpi_chart.measure_label'),
                                name: 'custom_label',
                                props: { placeholder: field.column_name },
                            },
                        })}
                    />
                </Box>
                <Box>
                    <FormLabel>{t('display_mask')}</FormLabel>
                    <ReactHookFormController
                        componentValues={[{ label: 'Default', value: '0' }].concat(
                            numericMasks.map((d) => ({
                                label: d.display_example,
                                value: d.display_mask_id,
                            })),
                        )}
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                name: 'display_mask_id',
                                component: 'FormSelect',
                            },
                        })}
                    />
                </Box>
                <Box>
                    <EditPanelCheckBox
                        elementProps={elementProps}
                        name={'show_data_labels'}
                        label={t('kpi_chart.display_values_on_chart')}
                    />
                </Box>
            </Stack>
            <Box flexShrink={0}>
                <BlockEditPanelControls
                    onApply={() => {
                        onApply({
                            ...field,
                            ...elementProps.form.hookFormGetValues(),
                        });
                    }}
                    onCancel={() => onCancel()}
                    isTypeSave
                />
            </Box>
        </Stack>
    );
}
